import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { supabase, subscriptions } from '../../lib/supabase';
import './AdminDashboard.css';

const AdminDashboard = ({ user }) => {
  console.log('🚀 AdminDashboard: Component mounted with user:', user?.email || 'No user');
  console.log('🏗️ AdminDashboard: Component is rendering');
  
  const [stats, setStats] = useState({
    totalQuestions: 0,
    pendingQuestions: 0,
    totalAnnouncements: 0,
    totalSessions: 0,
    totalBookmarks: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingItems, setPendingItems] = useState({
    questions: [],
    urls: []
  });
  const [pendingCounts, setPendingCounts] = useState({
    questions: 0,
    urls: 0
  });
  
  console.log('📊 AdminDashboard: Current state:', {
    loading,
    error,
    pendingQuestionsCount: pendingItems.questions.length,
    pendingUrlsCount: pendingItems.urls.length
  });

  const loadPendingItems = async () => {
    console.log('🔄 AdminDashboard: Loading pending items...');
    console.log('🔧 AdminDashboard: Supabase client check:', {
      clientExists: !!supabase,
      url: supabase?.supabaseUrl,
      key: supabase?.supabaseKey ? 'Present' : 'Missing'
    });
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('📋 AdminDashboard: Querying questions table...');
      // Load pending questions
      const { data: questions, error: questionsError } = await supabase
        .from('questions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (questionsError) {
        console.error('❌ AdminDashboard: Error loading questions:', questionsError);
        console.error('❌ AdminDashboard: Questions error details:', {
          message: questionsError.message,
          code: questionsError.code,
          details: questionsError.details,
          hint: questionsError.hint
        });
        throw questionsError;
      }

      console.log('✅ AdminDashboard: Questions query successful:', questions);
      console.log('🔗 AdminDashboard: Querying presenter_urls table...');
      
      // Load pending presenter URLs
      const { data: urls, error: urlsError } = await supabase
        .from('presenter_urls')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (urlsError) {
        console.error('❌ AdminDashboard: Error loading URLs:', urlsError);
        console.error('❌ AdminDashboard: URLs error details:', {
          message: urlsError.message,
          code: urlsError.code,
          details: urlsError.details,
          hint: urlsError.hint
        });
        throw urlsError;
      }

      console.log('✅ AdminDashboard: URLs query successful:', urls);
      console.log('✅ AdminDashboard: Pending items loaded successfully');
      console.log(`📊 AdminDashboard: Found ${questions?.length || 0} pending questions`);
      console.log(`📊 AdminDashboard: Found ${urls?.length || 0} pending URLs`);
      
      setPendingItems({
        questions: questions || [],
        urls: urls || []
      });
    } catch (error) {
      console.error('❌ AdminDashboard: Failed to load pending items:', error);
      console.error('❌ AdminDashboard: Full error object:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      setError('Failed to load pending items: ' + error.message);
    } finally {
      setLoading(false);
      console.log('🏁 AdminDashboard: loadPendingItems completed');
    }
  };

  useEffect(() => {
    // AdminDashboard: Initializing dashboard
    loadDashboardStats();
    loadPendingItems();
    const cleanup = setupNotifications();

    // Cleanup subscriptions on unmount
    return () => {
      // AdminDashboard: Cleaning up subscriptions
      if (cleanup) cleanup();
    };
  }, []);



  const loadDashboardStats = async () => {
    try {

      setLoading(true);
      setError('');

      // Load questions stats
      const { data: questions, error: questionsError } = await supabase
        .from('questions')
        .select('id, status, created_at');

      if (questionsError) {
        console.error('Error loading questions:', questionsError);
      }

      // Load announcements stats
      const { data: announcements, error: announcementsError } = await supabase
        .from('announcements')
        .select('id, created_at');

      if (announcementsError) {
        console.error('Error loading announcements:', announcementsError);
      }

      // Load sessions stats
      const { data: sessions, error: sessionsError } = await supabase
        .from('sessions')
        .select('id');

      if (sessionsError) {
        console.error('Error loading sessions:', sessionsError);
      }

      // Load bookmarks stats
      const { data: bookmarks, error: bookmarksError } = await supabase
        .from('bookmarks')
        .select('id');

      if (bookmarksError) {
        console.error('Error loading bookmarks:', bookmarksError);
      }

      // Calculate stats
      const totalQuestions = questions?.length || 0;
      const pendingQuestions = questions?.filter(q => q.status === 'pending').length || 0;
      const totalAnnouncements = announcements?.length || 0;
      const totalSessions = sessions?.length || 0;
      const totalBookmarks = bookmarks?.length || 0;

      // Get recent activity (last 10 questions)
      const recentActivity = questions
        ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        ?.slice(0, 10) || [];

      setStats({
        totalQuestions,
        pendingQuestions,
        totalAnnouncements,
        totalSessions,
        totalBookmarks,
        recentActivity
      });

      // Dashboard stats loaded successfully

      // Update pending counts
      setPendingCounts(prev => ({
        ...prev,
        questions: pendingQuestions
      }));

    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    // Parse the datetime string as local time to avoid timezone conversion
    let date;
    if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      // Parse as local time by manually extracting components
      const [datePart, timePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0];
      date = new Date(year, month - 1, day, hour, minute);
    } else {
      date = new Date(dateString);
    }

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-badge status-pending',
      approved: 'status-badge status-approved',
      rejected: 'status-badge status-rejected'
    };
    return statusClasses[status] || 'status-badge';
  };

  const setupNotifications = () => {
    // Setting up real-time notifications for admin

    // DIRECT SUPABASE SUBSCRIPTION TEST - bypassing our wrapper
    const directChannel = supabase
      .channel('direct-questions-test')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'questions'
        },
        (payload) => {
          // Direct Supabase event received
        }
      )
      .subscribe((status) => {
        // Direct subscription status updated
      });

    // Subscribe to all questions using the existing subscription system
    const unsubscribeQuestions = subscriptions.subscribeToQuestions('all', (payload) => {
      // New question event received for admin notification

      // Handle INSERT events (new questions) - payload is direct from Supabase
      // IMPORTANT: Supabase real-time events use 'eventType' property, not 'event'!
      // We need to detect INSERT by checking eventType or payload structure
      const eventType = payload.eventType || payload.event;
      const isInsertEvent = eventType === 'INSERT' || (payload.new && (!payload.old || Object.keys(payload.old).length === 0));

      // Raw Supabase event received for ALL questions

      // Event type detected and analyzed

      if (isInsertEvent && payload.new) {
        // New question details processed

        // Refresh pending items when new questions are submitted
        if (payload.new.status === 'pending') {
          // New pending question detected, refreshing
          refreshPendingItems();
        } else {
          // Question status is not pending
          // Still refresh stats for total counts
          loadDashboardStats();
        }
      } else {
        // Other event types (UPDATE/DELETE) handled
      }
    });

    // Subscribe to new presenter URLs with direct subscription
    const urlsChannelName = `admin-urls-${Date.now()}`;
    const urlsSubscription = supabase
      .channel(urlsChannelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'presenter_urls'
        },
        (payload) => {
          // New URL submitted, refreshing pending items
          if (payload.new) {
            // New URL detected, refreshing pending items
            refreshPendingItems();
          }
        }
      )
      .subscribe((status) => {
        // URLs subscription status updated
        if (status === 'SUBSCRIBED') {
          // Successfully subscribed to presenter_urls table changes
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to presenter_urls table');
        }
      });

    return () => {
      // Unsubscribing from notification channels
      if (unsubscribeQuestions) {
        // Unsubscribing from questions subscription
        unsubscribeQuestions();
      }
      if (urlsSubscription) {
        // Unsubscribing from URLs subscription
        urlsSubscription.unsubscribe();
      }
    };
  };

  const refreshPendingItems = () => {
    // Refreshing pending items due to real-time update
    loadPendingItems();
    loadDashboardStats();
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Welcome back, {user?.name || user?.email}</p>
        <div className="header-actions">
          <button
            onClick={loadDashboardStats}
            className="refresh-btn"
            title="Refresh dashboard data"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Pending Items Section - Always Visible */}
      <div className="pending-items-section">
        <div className="pending-header">
          <h2>Pending Review Items</h2>
          <div className="pending-summary">
            <span className="pending-count questions">
              ❓ {pendingCounts.questions} Questions
            </span>
            <span className="pending-count urls">
              🔗 {pendingCounts.urls} URLs
            </span>
          </div>
          <button
            className="refresh-btn"
            onClick={() => {
              loadDashboardStats();
              loadPendingItems();
            }}
            disabled={loading}
            title="Refresh pending items"
          >
            🔄 Refresh
          </button>
        </div>

        {(pendingItems.questions.length > 0 || pendingItems.urls.length > 0) ? (
          <div className="pending-items-list">
            {/* Pending Questions */}
            {pendingItems.questions.length > 0 && (
              <div className="pending-section">
                <h3>📋 Pending Questions ({pendingItems.questions.length})</h3>
                {pendingItems.questions.slice(0, 5).map((question) => (
                  <div key={`question-${question.id}`} className="pending-item question">
                    <div className="pending-icon">❓</div>
                    <div className="pending-content">
                      <div className="pending-title">Q&A Submission</div>
                      <div className="pending-message">
                        "{(question.text || 'No question text').substring(0, 80)}{(question.text || '').length > 80 ? '...' : ''}"
                      </div>
                      <div className="pending-time">
                        Submitted: {formatDate(question.created_at)}
                      </div>
                    </div>
                    <div className="pending-actions">
                      <button className="review-btn" onClick={() => window.location.hash = '#/admin?tab=questions'}>
                        Review
                      </button>
                    </div>
                  </div>
                ))}
                {pendingItems.questions.length > 5 && (
                  <div className="view-all-link">
                    <button onClick={() => window.location.hash = '#/admin?tab=questions'}>
                      View all {pendingItems.questions.length} pending questions →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Pending URLs */}
            {pendingItems.urls.length > 0 && (
              <div className="pending-section">
                <h3>🔗 Pending URLs ({pendingItems.urls.length})</h3>
                {pendingItems.urls.slice(0, 5).map((url) => (
                  <div key={`url-${url.id}`} className="pending-item url">
                    <div className="pending-icon">🔗</div>
                    <div className="pending-content">
                      <div className="pending-title">URL Submission</div>
                      <div className="pending-message">
                        {url.url} - Session {url.session_id}
                      </div>
                      <div className="pending-submitter">
                        By: {url.presenter_name}
                      </div>
                      <div className="pending-time">
                        Submitted: {formatDate(url.created_at)}
                      </div>
                    </div>
                    <div className="pending-actions">
                      <button className="review-btn" onClick={() => window.location.hash = '#/admin?tab=urls'}>
                        Review
                      </button>
                    </div>
                  </div>
                ))}
                {pendingItems.urls.length > 5 && (
                  <div className="view-all-link">
                    <button onClick={() => window.location.hash = '#/admin?tab=urls'}>
                      View all {pendingItems.urls.length} pending URLs →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="pending-empty">
            <p>✅ No pending items to review!</p>
            <p><small>All Q&A submissions and URL submissions have been processed.</small></p>
          </div>
        )}
      </div>


      {error && (
        <div className="alert alert-error">
          {typeof error === 'object' && error.message ? error.message : error}
        </div>
      )}

      <div className="stats-grid" data-testid="dashboard-stats">
        <div className="stat-card" data-testid="total-questions-stat">
          <div className="stat-icon">❓</div>
          <div className="stat-content">
            <h3>Total Questions</h3>
            <div className="stat-number">{stats.totalQuestions}</div>
          </div>
        </div>

        <div className="stat-card pending" data-testid="pending-questions-stat">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>Pending Questions</h3>
            <div className="stat-number">{stats.pendingQuestions}</div>
          </div>
        </div>

        <div className="stat-card" data-testid="announcements-stat">
          <div className="stat-icon">📢</div>
          <div className="stat-content">
            <h3>Announcements</h3>
            <div className="stat-number">{stats.totalAnnouncements}</div>
          </div>
        </div>

        <div className="stat-card" data-testid="sessions-stat">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>Sessions</h3>
            <div className="stat-number">{stats.totalSessions}</div>
          </div>
        </div>

        <div className="stat-card" data-testid="bookmarks-stat">
          <div className="stat-icon">🔖</div>
          <div className="stat-content">
            <h3>Bookmarks</h3>
            <div className="stat-number">{stats.totalBookmarks}</div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="quick-actions">
            <button className="action-btn primary">
              📝 Review Pending Questions
            </button>
            <button className="action-btn secondary">
              📢 Create Announcement
            </button>
            <button className="action-btn secondary">
              📊 View Analytics
            </button>
            <button className="action-btn secondary">
              🛡️ Content Moderation
            </button>
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Recent Activity</h2>
          {stats.recentActivity.length > 0 ? (
            <div className="activity-list">
              {stats.recentActivity.map((activity, index) => (
                <div key={activity.id || `activity-${index}`} className="activity-item">
                  <div className="activity-content">
                    <span className="activity-type">Question submitted</span>
                    <span className="activity-time">{formatDate(activity.created_at)}</span>
                  </div>
                  <span className={getStatusBadge(activity.status)}>
                    {activity.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-activity">
              <p>No recent activity</p>
            </div>
          )}
        </div>

        <div className="dashboard-section">
          <h2>System Status</h2>
          <div className="system-status">
            <div className="status-item">
              <span className="status-indicator online" />
              <span>Database Connection</span>
              <span className="status-text">Online</span>
            </div>
            <div className="status-item">
              <span className="status-indicator online" />
              <span>Authentication Service</span>
              <span className="status-text">Online</span>
            </div>
            <div className="status-item">
              <span className="status-indicator online" />
              <span>File Storage</span>
              <span className="status-text">Online</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AdminDashboard.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string
  })
};

export default AdminDashboard;
